<template>
  <!-- Autocomplete agence, calqué sur EmployeeAutocomplete. Différence de fond : les 644
       agences tiennent dans le store (agency/getAllForSelect), la recherche reste donc
       locale — pas d'aller-retour serveur à la frappe. -->
  <v-select
    :value="selectedOption"
    :options="results"
    :filterable="false"
    label="label"
    :placeholder="placeholder"
    :disabled="disabled"
    class="agency-ac"
    :class="'agency-ac--' + variant"
    @input="onInput"
    @search="onSearch"
  >
    <!-- Format « CODE - Nom », identique à la colonne Agence de la liste. -->
    <template slot="option" slot-scope="opt">
      <div class="agency-ac__opt">
        <span class="agency-ac__code">{{ opt.code }}</span>
        {{ opt.name }}
      </div>
    </template>

    <template slot="selected-option" slot-scope="opt">
      <span class="agency-ac__label">{{ opt.label }}</span>
    </template>

    <template slot="no-options">
      <div class="agency-ac__empty">Aucune agence pour cette recherche</div>
    </template>

    <li v-if="hiddenCount > 0" slot="list-footer" class="agency-ac__more">
      <i class="far fa-ellipsis-h"></i> {{ hiddenCount.toLocaleString("fr-FR") }} autre(s) —
      {{ currentQuery ? "précisez la recherche" : "saisissez pour rechercher" }}
    </li>
  </v-select>
</template>

<script>
// Nombre d'agences rendues dans le menu, aussi bien à l'ouverture du champ que pour les
// résultats d'une recherche. Sans plafond, l'ouverture monterait les 644 agences d'un coup.
const LIMIT = 20;

// Minuscule + suppression des accents, pour un matching tolérant.
function normalize(s) {
  return (s == null ? "" : String(s))
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // marques diacritiques combinantes
}

export default {
  name: "AgencyAutocomplete",
  props: {
    value: { default: null },
    placeholder: { type: String, default: "Code ou nom d'agence…" },
    // Non utilisé : le composant charge lui-même son référentiel. Déclaré parce qu'AdminForm
    // passe `options` uniformément à tous les champs 'autocomplete' — sans la prop, la liste
    // atterrirait en attribut DOM sur le v-select.
    options: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
    // 'filter' : champ compact d'une ligne de filtres. 'form' : le champ reprend
    // l'habillage global des champs de formulaire.
    variant: { type: String, default: "filter" },
  },
  data() {
    return {
      results: [],
      hiddenCount: 0,
      // Dernière expression recherchée : distingue « le champ est vide » de « la recherche
      // n'a rien donné », pour le pied de liste.
      currentQuery: "",
    };
  },
  computed: {
    agencies() {
      return this.$store.getters["agency/getAll"] || [];
    },
    /** Index normalisé, recalculé seulement si la liste du store change. */
    index() {
      return this.agencies
        .map((a) => {
          const code = a.internal_number || "";
          const name = a.name || "";
          return {
            value: a.id,
            code: code,
            name: name,
            label: code && name ? code + " - " + name : code || name,
            hay: normalize(code + " " + name),
            codeHay: normalize(code),
          };
        })
        .sort((a, b) => a.label.localeCompare(b.label));
    },
    // Cherchée dans l'index complet et non dans `results`, pour que le libellé s'affiche
    // même hors résultats filtrés. Comparaison souple : une valeur restaurée depuis l'URL
    // arrive sous forme de chaîne.
    selectedOption() {
      if (this.value == null || this.value === "") return null;
      return this.index.find((o) => String(o.value) === String(this.value)) || null;
    },
  },
  watch: {
    // Le store peut se remplir après le montage : on rafraîchit la liste proposée.
    index() {
      this.computeResults(this.currentQuery);
    },
  },
  mounted() {
    // Autonome : le composant ne suppose pas que la vue hôte a chargé les agences.
    this.$store.dispatch("agency/getAllForSelect");
    this.computeResults("");
  },
  methods: {
    normalize,
    onInput(option) {
      this.$emit("input", option ? option.value : null);
    },
    /**
     * v-select émet `search` à chaque frappe, et aussi à la remise à zéro après sélection.
     * On ne s'accroche volontairement pas à `open` : cet événement part APRÈS `search`
     * lorsque la frappe ouvre le menu, et y recalculer la liste effacerait le filtrage.
     */
    onSearch(search) {
      this.currentQuery = normalize(search).trim();
      this.computeResults(this.currentQuery);
    },
    computeResults(q) {
      if (!q) {
        this.hiddenCount = Math.max(0, this.index.length - LIMIT);
        this.results = this.index.slice(0, LIMIT);
        return;
      }

      const tokens = q.split(/\s+/);
      const scored = [];
      for (const item of this.index) {
        const s = this.scoreItem(item, tokens);
        if (s > 0) scored.push({ item, s });
      }
      scored.sort((a, b) => b.s - a.s || a.item.label.localeCompare(b.item.label));
      this.hiddenCount = Math.max(0, scored.length - LIMIT);
      this.results = scored.slice(0, LIMIT).map((x) => x.item);
    },
    // Tous les mots de la requête doivent matcher ; le score total classe.
    scoreItem(item, tokens) {
      let total = 0;
      for (const t of tokens) {
        const s = this.scoreToken(item, t);
        if (s <= 0) return 0;
        total += s;
      }
      return total;
    },
    // Priorité : code exact/préfixe > sous-chaîne (plus tôt = mieux) > sous-séquence.
    scoreToken(item, t) {
      if (item.codeHay === t) return 200;
      if (item.codeHay.indexOf(t) === 0) return 120;
      const idx = item.hay.indexOf(t);
      if (idx === 0) return 100;
      if (idx > 0) return 60 - Math.min(idx, 30);
      return this.isSubsequence(item.hay, t) ? 15 : 0;
    },
    isSubsequence(hay, t) {
      let h = 0;
      for (let i = 0; i < t.length; i++) {
        h = hay.indexOf(t[i], h);
        if (h === -1) return false;
        h++;
      }
      return true;
    },
  },
};
</script>

<style scoped>
/* Racine bornée à la largeur de la colonne, sinon le toggle grandit avec le
   contenu et la valeur déborde du bloc (l'ellipsis ne peut pas se déclencher). */
.agency-ac {
  width: 100%;
}
.agency-ac >>> .vs__dropdown-toggle {
  background: #fff;
  max-width: 100%;
  align-items: stretch;
}
.agency-ac >>> .vs__selected-options {
  align-items: center;
  flex-wrap: nowrap;
  min-width: 0;
  overflow: hidden;
  padding: 0 2px;
}
.agency-ac >>> .vs__selected {
  margin: 0 2px;
  max-width: 100%;
  flex-wrap: nowrap;
  overflow: hidden;
}
/* Valeur sélectionnée : forcée sur une seule ligne, tronquée par « … ». */
.agency-ac >>> .vs__selected .agency-ac__label {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.agency-ac >>> .vs__search {
  margin: 0;
}
.agency-ac >>> .vs__actions {
  padding-top: 0;
}
.agency-ac >>> .vs__dropdown-menu {
  padding: 4px 0;
}
.agency-ac >>> .vs__dropdown-option {
  padding: 6px 12px;
}
.agency-ac >>> .vs__dropdown-option--highlight {
  background: #1ab394;
  color: #fff;
}

/* Ligne de filtres : champ compact, à la hauteur des autres filtres. */
.agency-ac--filter >>> .vs__dropdown-toggle {
  border-color: #e5e6e7;
  border-radius: 3px;
  min-height: 31px;
  padding: 0; /* défaut asymétrique 0 0 4px → casse le centrage */
}
.agency-ac--filter >>> .vs__selected,
.agency-ac--filter >>> .vs__search {
  font-size: 13px;
}
.agency-ac--filter .agency-ac__opt {
  font-size: 13px;
}

/* Formulaire : hauteur, bordure et retrait de 20px viennent de l'habillage global des
   .vs__dropdown-toggle. On ne corrige que le padding vertical asymétrique de vue-select,
   qui décentrerait la valeur. */
.agency-ac--form >>> .vs__dropdown-toggle {
  padding: 0 0 0 20px;
}
/* Le menu déroulant est hors du champ : il n'hérite pas des 12px du formulaire mais des
   10px du body. */
.agency-ac--form .agency-ac__opt {
  font-size: 12px;
}

.agency-ac__opt {
  white-space: normal;
  color: #34495e;
}
.agency-ac >>> .vs__dropdown-option--highlight .agency-ac__opt {
  color: #fff;
}
.agency-ac__label {
  color: #34495e;
}
/* Code agence : mis en avant, c'est par lui qu'on cherche le plus souvent. */
.agency-ac__code {
  color: #909499;
  font-size: 12px;
  margin-right: 4px;
}
.agency-ac >>> .vs__dropdown-option--highlight .agency-ac__code {
  color: #e8f7f3;
}
.agency-ac__empty {
  padding: 6px 4px;
  color: #909499;
  font-size: 13px;
}
.agency-ac__more {
  list-style: none;
  padding: 6px 12px;
  font-size: 12px;
  color: #909499;
  border-top: 1px solid #f1f3f5;
  background: #fafbfc;
}
.agency-ac__more i {
  margin-right: 4px;
}
</style>



// WEBPACK FOOTER //
// src/components/Global/AgencyAutocomplete.vue